import { useEffect, useState } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import {
  Award, CheckCircle2, XCircle, Clock, Search, Package, TrendingUp,
  IndianRupee, Filter, AlertCircle, CreditCard, FileText, Pause, RefreshCw,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ApprovalActionDialog, canApprove } from "@/components/ApprovalActionDialog";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

const STATUS_CONFIG = {
  PENDING_APPROVAL: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock, label: "Pending Review" },
  APPROVED: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle2, label: "Approved" },
  REJECTED: { color: "bg-red-100 text-red-700 border-red-200", icon: XCircle, label: "Rejected" },
  PAID: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CreditCard, label: "Paid" },
  ON_HOLD: { color: "bg-orange-100 text-orange-700 border-orange-200", icon: Pause, label: "On Hold" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: "bg-slate-100 text-slate-600", icon: AlertCircle, label: status };
  const Icon = cfg.icon;
  return (
    <Badge className={`text-[10px] px-1.5 py-0 gap-1 ${cfg.color}`} data-testid={`claim-status-${status}`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </Badge>
  );
};

const SchemeClaimsPage = () => {
  const { user } = useAuth();
  const canApproveClaims = canApprove(user);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("PENDING_APPROVAL");
  const [approvalDialog, setApprovalDialog] = useState(null); // { entityId, entityLabel, action }
  const [payClaim, setPayClaim] = useState(null);
  const [payReference, setPayReference] = useState("");
  const [acting, setActing] = useState(null);

  const fetchClaims = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const qs = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await authFetch(`${API}/scheme-claims${qs}`);
      const d = await res.json();
      if (d.success) {
        setClaims(d.data || []);
        setStats(d.meta?.stats || {});
      }
    } catch {
      if (!silent) toast.error("Failed to load claims");
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClaims(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const { lastUpdatedAt, refreshing, manualRefresh } = useAutoRefresh(fetchClaims, {
    user,
    eventTypes: ["scheme_claim", "claim", "scheme"],
    pollIntervalMs: 30000,
  });

  const submitPay = async () => {
    if (!payClaim) return;
    setActing(payClaim.claim_id);
    try {
      const res = await authFetch(`${API}/scheme-claims/${payClaim.claim_id}/pay`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_reference: payReference }),
      });
      const d = await res.json();
      if (d.success) { toast.success("Claim marked paid"); setPayClaim(null); setPayReference(""); fetchClaims(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setActing(null); }
  };

  const filtered = filterByRegion(claims, (c) => c.region_id).filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.party_name || "").toLowerCase().includes(q) ||
      (c.scheme_name || "").toLowerCase().includes(q) ||
      (c.claim_id || "").toLowerCase().includes(q);
  });

  const openRewardLetter = async (claimId) => {
    // Fetch letter HTML with auth header and inject into new window (since anchor href can't send headers).
    try {
      const res = await authFetch(`${API}/scheme-claims/${claimId}/reward-letter.html`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to generate letter");
        return;
      }
      const html = await res.text();
      const win = window.open("", "_blank", "width=900,height=1100");
      if (!win) { toast.error("Pop-up blocked - allow pop-ups to print the letter"); return; }
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (e) { toast.error("Network error"); }
  };

  const pendingCount = stats.PENDING_APPROVAL?.count || 0;
  const pendingValue = (stats.PENDING_APPROVAL?.value || 0) / 100;
  const approvedValue = (stats.APPROVED?.value || 0) / 100;
  const paidValue = (stats.PAID?.value || 0) / 100;

  return (
    <div className="space-y-6" data-testid="scheme-claims-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora'] flex items-center gap-2">
            <Award className="w-6 h-6 text-[#8B1A1A]" /> Scheme Claims
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
            Review and approve dealer claims created by auto-tally when schemes close.
            {lastUpdatedAt && (
              <span className="text-[10px] text-slate-400">
                · Last updated {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="scheme-claims-region-switcher"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            className="h-9"
            data-testid="scheme-claims-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="stat-pending">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-amber-500" /><p className="text-xs uppercase text-slate-500">Pending</p></div>
          <p className="text-2xl font-bold font-mono text-amber-600">{pendingCount}</p>
          <p className="text-xs text-slate-500 mt-1">₹{pendingValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>
        </Card>
        <Card className="p-4" data-testid="stat-approved">
          <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-blue-500" /><p className="text-xs uppercase text-slate-500">Approved</p></div>
          <p className="text-2xl font-bold font-mono text-blue-600">{stats.APPROVED?.count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">₹{approvedValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>
        </Card>
        <Card className="p-4" data-testid="stat-paid">
          <div className="flex items-center gap-2 mb-1"><IndianRupee className="w-4 h-4 text-emerald-500" /><p className="text-xs uppercase text-slate-500">Paid Out</p></div>
          <p className="text-2xl font-bold font-mono text-emerald-600">{stats.PAID?.count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">₹{paidValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>
        </Card>
        <Card className="p-4" data-testid="stat-rejected">
          <div className="flex items-center gap-2 mb-1"><XCircle className="w-4 h-4 text-red-500" /><p className="text-xs uppercase text-slate-500">Rejected</p></div>
          <p className="text-2xl font-bold font-mono text-red-600">{stats.REJECTED?.count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">declined claims</p>
        </Card>
      </div>

      {/* Pending alert */}
      {pendingCount > 0 && statusFilter !== "PENDING_APPROVAL" && (
        <Card className="p-3 border-amber-200 bg-amber-50 flex items-center gap-2" data-testid="pending-claims-alert">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <p className="text-sm text-amber-700 flex-1">{pendingCount} claim{pendingCount > 1 ? "s" : ""} awaiting review.</p>
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" onClick={() => setStatusFilter("PENDING_APPROVAL")}>Review Now</Button>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by dealer / scheme / claim ID" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-claims" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="claim-status-filter"><Filter className="w-3.5 h-3.5 mr-2" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="PENDING_APPROVAL">Pending Review</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="PAID">Paid</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-slate-400">{filtered.length} claims</span>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table text-sm" data-testid="claims-table">
            <thead>
              <tr>
                <th>Claim ID</th>
                <th>Dealer</th>
                <th>Scheme</th>
                <th className="text-right">Bags</th>
                <th className="text-right">Sales Amount</th>
                <th className="text-right">Claim Value</th>
                <th>Status</th>
                <th>Tallied At</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No claims in this view
                </td></tr>
              ) : filtered.map((c) => (
                <tr key={c.claim_id} data-testid={`claim-row-${c.claim_id}`}>
                  <td className="font-mono text-[11px] text-slate-500">{c.claim_id}</td>
                  <td>
                    <div className="font-medium text-slate-900">{c.party_name || c.party_id}</div>
                    <div className="text-[10px] text-slate-400">{c.party_id}</div>
                  </td>
                  <td>
                    <div className="font-medium">{c.scheme_name}</div>
                    <div className="text-[10px] text-slate-400">{c.reward_type} · {c.reward_value}</div>
                  </td>
                  <td className="text-right font-mono">{c.total_bags}</td>
                  <td className="text-right font-mono">₹{((c.total_amount || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                  <td className="text-right font-mono font-bold text-[#8B1A1A]">₹{((c.claim_value || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="text-xs text-slate-500">{c.tallied_at ? new Date(c.tallied_at).toLocaleDateString("en-IN") : "-"}</td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      {(c.status === "PENDING_APPROVAL" || c.status === "ON_HOLD") && canApproveClaims && (
                        <>
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                            onClick={() => setApprovalDialog({ entityId: c.claim_id, entityLabel: `${c.party_name} · ${c.scheme_name} · ₹${((c.claim_value || 0) / 100).toLocaleString("en-IN")}`, action: "APPROVE" })}
                            disabled={acting === c.claim_id} data-testid={`approve-${c.claim_id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          {c.status === "PENDING_APPROVAL" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs border-amber-200 text-amber-600 hover:bg-amber-50"
                              onClick={() => setApprovalDialog({ entityId: c.claim_id, entityLabel: `${c.party_name} · ${c.scheme_name} · ₹${((c.claim_value || 0) / 100).toLocaleString("en-IN")}`, action: "HOLD" })}
                              data-testid={`hold-${c.claim_id}`} title="Hold">
                              <Pause className="w-3 h-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => setApprovalDialog({ entityId: c.claim_id, entityLabel: `${c.party_name} · ${c.scheme_name} · ₹${((c.claim_value || 0) / 100).toLocaleString("en-IN")}`, action: "REJECT" })}
                            data-testid={`reject-${c.claim_id}`} title="Reject">
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {c.status === "APPROVED" && (
                        <>
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => { setPayClaim(c); setPayReference(""); }} data-testid={`pay-${c.claim_id}`}>
                            <CreditCard className="w-3 h-3 mr-1" /> Mark Paid
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openRewardLetter(c.claim_id)} title="Reward Letter" data-testid={`letter-${c.claim_id}`}>
                            <FileText className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {c.status === "PAID" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openRewardLetter(c.claim_id)} data-testid={`letter-${c.claim_id}`}>
                          <FileText className="w-3 h-3 mr-1" /> Letter
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Unified Approve/Reject/Hold Dialog */}
      {approvalDialog && (
        <ApprovalActionDialog
          open={!!approvalDialog}
          onClose={() => setApprovalDialog(null)}
          entity="scheme_claim"
          action={approvalDialog.action}
          entityId={approvalDialog.entityId}
          entityLabel={approvalDialog.entityLabel}
          endpointBase="/scheme-claims"
          onSuccess={() => fetchClaims()}
        />
      )}

      {/* Pay Dialog */}
      <Dialog open={!!payClaim} onOpenChange={(o) => { if (!o) { setPayClaim(null); setPayReference(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Mark Claim as Paid</DialogTitle>
            <DialogDescription>Record the UTR / cheque number / voucher reference for audit.</DialogDescription>
          </DialogHeader>
          {payClaim && (
            <div className="space-y-3 py-2">
              <Card className="p-3 bg-emerald-50 border-emerald-200">
                <p className="text-xs text-emerald-700">Paying out to dealer</p>
                <p className="font-bold text-emerald-900">{payClaim.party_name}</p>
                <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">₹{((payClaim.claim_value || 0) / 100).toLocaleString("en-IN")}</p>
              </Card>
              <Input placeholder="Payment reference (UTR / cheque no / voucher)" value={payReference} onChange={(e) => setPayReference(e.target.value)} data-testid="payment-reference" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setPayClaim(null)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitPay} disabled={acting === payClaim.claim_id} data-testid="confirm-pay">
                  {acting === payClaim.claim_id ? "Marking..." : "Confirm Payment"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchemeClaimsPage;
